import {
  getChangeFinalizedPlanningStatusKey,
  getCurrentLanguage,
  getCurrentValuesSystem,
  getHomeFiltersDates,
  getMonitorsFilters,
  getMyNegotiationsTypesFiltersFieldOrder,
  getPortfolioFiltersParameters,
  getPortfolioFiltersShowPhotograph,
  getProgressFiltersParameters,
  getReportFiltersGroupsCategoriesIds,
  getReportFiltersRangeDate,
  getReportFiltersUsersIds,
  getReportFiltersUsersStatus,
  setChangeFinalizedPlanningStatusKey,
  setCurrentLanguage,
  setCurrentValuesSystem,
  setHomeFiltersDates,
  setMonitorsFilters,
  setMyNegotiationsTypesFiltersFieldOrder,
  setPortfolioFiltersParameters,
  setPortfolioFiltersShowPhotograph,
  setProgressFiltersParameters,
  setReportFiltersGroupsCategoriesIds,
  setReportFiltersRangeDate,
  setReportFiltersUsersIds,
  setReportFiltersUsersStatus,
} from '@helpers/utils';
import {
  CHANGE_FINALIZED_PLANNING_STATUS,
  CHANGE_HOME_FILTERS_DATES,
  CHANGE_LOCALE,
  CHANGE_MONITORS_FILTERS_PARAMETERS,
  CHANGE_MY_NEGOTIATIONS_TYPES_FIELD_ORDER,
  CHANGE_PORTFOLIO_FILTERS_PARAMETERS,
  CHANGE_PORTFOLIO_FILTERS_SHOW_PHOTOGRAPH,
  CHANGE_PROGRESS_FILTERS_PARAMETERS,
  CHANGE_REPORT_FILTERS_GROUPS_CATEGORIES,
  CHANGE_REPORT_FILTERS_RANGE_DATE,
  CHANGE_REPORT_FILTERS_USERS,
  CHANGE_REPORT_FILTERS_USERS_STATUS,
  UPDATE_VALUES_SYSTEM,
} from '../../constants/ActionTypes';
import { IDataReducer } from '../../models/generals/iDataReducer';
import SettingsClass, { ISettings } from '../../models/redux/iSettings';
import ReportFiltersClass from '../../models/generals/iReportFilters';
import PortfolioFiltersClass from '../../models/generals/iPortfolioFilter';
import { FilterUserEnum, PeriodsChartEnum, QuestionEnum, validityStatusEnum } from '@helpers/enums';
import HomeFiltersClass from '../../models/generals/iHomeFilters';
import GeneralsHelper from '@helpers/generalsHelper';
import MyNegotiationsTypesFiltersClass from '../../models/generals/iMyNegotiationsTypesFilters';
import ProgressFiltersClass from '../../models/generals/iProgressFilter';
import MonitorsFiltersClass from '@models/generals/iMonitorsFiltersClass';

const INIT_STATE = new SettingsClass(
  getCurrentLanguage(),
  getCurrentValuesSystem(),
  new ReportFiltersClass(
    getReportFiltersUsersIds(),
    getReportFiltersGroupsCategoriesIds(),
    getReportFiltersRangeDate(),
    getReportFiltersUsersStatus(),
    getChangeFinalizedPlanningStatusKey(),
  ),
  new PortfolioFiltersClass(getPortfolioFiltersShowPhotograph(), getPortfolioFiltersParameters()),
  new MonitorsFiltersClass(getMonitorsFilters()),
  new HomeFiltersClass(getHomeFiltersDates()),
  new MyNegotiationsTypesFiltersClass(getMyNegotiationsTypesFiltersFieldOrder()),
  new ProgressFiltersClass(getProgressFiltersParameters()),
);

export default (state = INIT_STATE, action: IDataReducer): ISettings => {
  switch (action.type) {
    case CHANGE_LOCALE:
      setCurrentLanguage(action.locale);
      return { ...state, locale: action.locale ?? 'es' };

    case UPDATE_VALUES_SYSTEM:
      setCurrentValuesSystem(action.valuesSystem);
      return { ...state, valuesSystem: action.valuesSystem ?? [] };

    case CHANGE_REPORT_FILTERS_USERS:
      setReportFiltersUsersIds(action.usersId);
      return { ...state, reportFiltersStorage: { ...state.reportFiltersStorage, users_id: action.usersId ?? [] } };

    case CHANGE_REPORT_FILTERS_USERS_STATUS:
      setReportFiltersUsersStatus(action.filterUserEnum);
      return {
        ...state,
        reportFiltersStorage: {
          ...state.reportFiltersStorage,
          filterUserEnum: action.filterUserEnum ?? FilterUserEnum.ACTIVE,
        },
      };

    case CHANGE_REPORT_FILTERS_GROUPS_CATEGORIES:
      setReportFiltersGroupsCategoriesIds(action.filterGroupsCategories);
      return {
        ...state,
        reportFiltersStorage: { ...state.reportFiltersStorage, groups_categories_id: action.filterGroupsCategories ?? [] },
      };

    case CHANGE_REPORT_FILTERS_RANGE_DATE:
      setReportFiltersRangeDate(action.rangeDate);
      return { ...state, reportFiltersStorage: { ...state.reportFiltersStorage, range_date: action.rangeDate ?? null } };
    case CHANGE_PORTFOLIO_FILTERS_SHOW_PHOTOGRAPH:
      setPortfolioFiltersShowPhotograph(action.showPhotograph);
      return {
        ...state,
        portfolioFiltersStorage: {
          ...state.portfolioFiltersStorage,
          showPhotograph: action.showPhotograph ?? QuestionEnum.NO,
        },
      };
    case CHANGE_PORTFOLIO_FILTERS_PARAMETERS:
      setPortfolioFiltersParameters(action.parameters);
      return {
        ...state,
        portfolioFiltersStorage: {
          ...state.portfolioFiltersStorage,
          parameters: action.parameters ?? {
            negotiations_types_id: [],
            negotiations_types_statuses_id: [],
            batches_id: [],
            searchText: '',
            entityListType: '',
            entities_type_id: 0,
            filters: [],
          },
        },
      };
    case CHANGE_MONITORS_FILTERS_PARAMETERS:
      setMonitorsFilters(action.parameters);
      return {
        ...state,
        monitorsFiltersStorage: {
          ...state.monitorsFiltersStorage,
          parameters: action.parameters ?? {},
        },
      };
    case CHANGE_PROGRESS_FILTERS_PARAMETERS:
      const progressDateObject = GeneralsHelper.getDateByType(PeriodsChartEnum.CURRENT_MONTH_TODAY);
      const rangeDate = {
        type: progressDateObject?.type,
        startDate: progressDateObject?.startDate.format('yyyy-MM-DD') ?? '',
        endDate: progressDateObject?.endDate.format('yyyy-MM-DD') ?? '',
      };
      const progressParameters = {
        range_date: rangeDate,
        users_id: [],
        groups_categories_id: [],
        filterUserEnum: FilterUserEnum.ACTIVE,
        batches_id: [],
        validity: validityStatusEnum.ALL,
      };
      setProgressFiltersParameters(action.parameters);
      return {
        ...state,
        progressFiltersStorage: {
          ...state.progressFiltersStorage,
          parameters: action.parameters ?? progressParameters,
        },
      };

    case CHANGE_HOME_FILTERS_DATES:
      setHomeFiltersDates(action.dates);
      const dateObject = GeneralsHelper.getDateByType(PeriodsChartEnum.CURRENT_MONTH);
      const baseParameters = {
        label: dateObject?.type.label,
        value: dateObject?.type.value,
        type: dateObject?.type.value,
        startDate: dateObject?.startDate,
        endDate: dateObject?.endDate,
      };
      return {
        ...state,
        homeFiltersStorage: {
          ...state.homeFiltersStorage,
          dates: action.dates ?? baseParameters,
        },
      };
    case CHANGE_MY_NEGOTIATIONS_TYPES_FIELD_ORDER:
      setMyNegotiationsTypesFiltersFieldOrder(action.fieldOrder);

      return {
        ...state,
        myNegotiationsTypesFiltersStorage: {
          ...state.homeFiltersStorage,
          fieldOrder: action.fieldOrder ?? null,
        },
      };
    case CHANGE_FINALIZED_PLANNING_STATUS:
      setChangeFinalizedPlanningStatusKey(action.filterStatusFinishedEnum);
      return {
        ...state,
        filterStatusFinishedEnum: action.filterStatusFinishedEnum ?? [],
      };

    default:
      return state;
  }
};
